import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { motion } from 'framer-motion';
import { Search, MapPin, ArrowLeft, Satellite } from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './MapaUnidades.css';
import { unidadesData, defaultMapCenter, defaultMapZoom, UnidadeData } from '../data/unidadesData';

// Configuração dos ícones do Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});



interface MapaUnidadesProps {
  onVoltar: () => void;
}



// Componente para controlar o centro do mapa
const MapController: React.FC<{ center: [number, number], zoom: number }> = ({ center, zoom }) => {
  const map = useMap();
  
  useEffect(() => {
    map.setView(center, zoom);
  }, [map, center, zoom]);
  
  return null;
};

const MapaUnidades: React.FC<MapaUnidadesProps> = ({ onVoltar }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredUnidades, setFilteredUnidades] = useState<UnidadeData[]>([]);
  const [mapCenter, setMapCenter] = useState<[number, number]>(defaultMapCenter);
  const [mapZoom, setMapZoom] = useState(defaultMapZoom);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isSatelliteView, setIsSatelliteView] = useState(false);
  const [satelliteProvider, setSatelliteProvider] = useState<'esri' | 'google' | 'fallback'>('esri');

  // Filtrar unidades baseado no termo de busca
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredUnidades(unidadesData);
      setShowSearchResults(false);
    } else {
      const filtered = unidadesData.filter((unidade: UnidadeData) =>
        unidade.nome_unidade.toLowerCase().includes(searchTerm.toLowerCase()) ||
        unidade.sigla_unidade.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredUnidades(filtered);
      setShowSearchResults(true);
    }
  }, [searchTerm]);

  const handleUnidadeSelect = (unidade: UnidadeData) => {
    setMapCenter([unidade.latitude, unidade.longitude]);
    setMapZoom(17);
    setShowSearchResults(false);
    setSearchTerm('');
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (filteredUnidades.length > 0) {
      handleUnidadeSelect(filteredUnidades[0]);
    }
  };

  const handleSatelliteToggle = () => {
    setIsSatelliteView(!isSatelliteView);
    if (!isSatelliteView) {
      // Reset para o provedor padrão quando ativar satélite
      setSatelliteProvider('esri');
    }
  };

  const handleSatelliteError = () => {
    // Fallback automático para outro provedor se houver erro
    if (satelliteProvider === 'esri') {
      setSatelliteProvider('google');
    } else if (satelliteProvider === 'google') {
      setSatelliteProvider('fallback');
    } else {
      // Se todos falharem, volta para mapa normal
      setIsSatelliteView(false);
    }
  };

  // Fechar lista de resultados quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const searchContainer = document.querySelector('.search-container');
      
      // Fechar lista de resultados
      if (searchContainer && !searchContainer.contains(event.target as Node)) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="mapa-unidades">
      <motion.div 
        className="mapa-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <button className="btn-voltar" onClick={onVoltar}>
          <ArrowLeft size={20} />
          Voltar
        </button>
        <h1>Mapa de Unidades UNIFAP</h1>
        <p>Encontre a localização das unidades da Universidade Federal do Amapá</p>
      </motion.div>

      <motion.div 
        className="mapa-container"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <div className="search-container">
          <form onSubmit={handleSearchSubmit} className="search-form">
            <div className="search-input-wrapper">
              <Search size={20} className="search-icon" />
              <input
                type="text"
                placeholder="Digite o nome ou sigla da unidade..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
          </form>

          {showSearchResults && (
            <motion.div 
              className="search-results"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              {filteredUnidades.length > 0 ? (
                filteredUnidades.map((unidade) => (
                  <div
                    key={unidade.id}
                    className="search-result-item"
                    onClick={() => handleUnidadeSelect(unidade)}
                  >
                    <MapPin size={16} />
                    <div>
                      <strong>{unidade.sigla_unidade}</strong>
                      <p>{unidade.nome_unidade}</p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-results">
                  <p>Nenhuma unidade encontrada</p>
                </div>
              )}
            </motion.div>
          )}
        </div>

        <div className="map-wrapper">
          <button 
            className="map-toggle-btn"
            onClick={handleSatelliteToggle}
            title={isSatelliteView ? "Mudar para mapa normal" : "Mudar para satélite"}
          >
            <Satellite size={20} />
            <span>{isSatelliteView ? "Mapa" : "Satélite"}</span>
          </button>
          
                      {isSatelliteView && (
              <div className="satellite-provider-info">
                <small>
                  Provedor: {satelliteProvider === 'esri' ? 'Mapbox' : 
                            satelliteProvider === 'google' ? 'Google Maps' : 'OpenStreetMap'}
                </small>
              </div>
            )}
          
          <MapContainer
            center={mapCenter}
            zoom={mapZoom}
            scrollWheelZoom={true}
            className="leaflet-map"
          >
            <MapController center={mapCenter} zoom={mapZoom} />
            {isSatelliteView ? (
              <>
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  opacity={0.3}
                />

                {satelliteProvider === 'esri' && (
                  <TileLayer
                    attribution='&copy; <a href="https://www.mapbox.com/">Mapbox</a>'
                    url="https://api.mapbox.com/styles/v1/mapbox/satellite-v9/tiles/{z}/{x}/{y}?access_token=pk.eyJ1IjoibWFwYm94IiwiYSI6ImNpejY4NXVycTA2emYycXBndHRqcmZ3N3gifQ.rJcFIG214AriISLbB6B5aw"
                    maxZoom={22}
                    eventHandlers={{
                      tileerror: handleSatelliteError
                    }}
                  />
                )}
                {satelliteProvider === 'google' && (
                  <TileLayer
                    attribution='&copy; <a href="https://www.google.com/maps">Google Maps</a>'
                    url="https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}"
                    maxZoom={20}
                    eventHandlers={{
                      tileerror: handleSatelliteError
                    }}
                  />
                )}
                {satelliteProvider === 'fallback' && (
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    opacity={0.8}
                  />
                )}
              </>
            ) : (
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
            )}
            {filteredUnidades.map((unidade) => (
              <Marker
                key={unidade.id}
                position={[unidade.latitude, unidade.longitude]}
              >
                <Popup 
                  closeButton={false}
                  autoClose={true}
                  closeOnEscapeKey={true}
                  closeOnClick={false}
                  className="custom-popup"
                >
                  <div className="popup-content">
                    
                    <div className="popup-header">
                      <h3>{unidade.sigla_unidade}</h3>
                      <span className="popup-precision">±{unidade.accuracy}m</span>
                    </div>
                    <p>{unidade.nome_unidade}</p>
                    <small>Fonte: {unidade.source}</small>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>


      </motion.div>
    </div>
  );
};

export default MapaUnidades;
